/**
 * Shared animation configurations for Framer Motion
 *
 * This file centralizes all animation variants and spring configurations
 * to maintain consistency across the app and enable global animation tuning.
 */

import type { Variants } from "framer-motion";

// ============================================================================
// SPRING CONFIGURATIONS
// ============================================================================

/**
 * Default spring physics configuration
 * Used for smooth, natural animations throughout the app
 */
export const springConfig = {
  type: "spring" as const,
  stiffness: 300,
  damping: 28,
};

/**
 * Snappy spring for quick, responsive interactions
 * Used for UI elements that need immediate feedback
 */
export const springSnappy = {
  type: "spring" as const,
  stiffness: 400,
  damping: 30,
};

/**
 * Gentle spring for subtle transitions
 * Used for background elements and low-priority animations
 */
export const springGentle = {
  type: "spring" as const,
  stiffness: 200,
  damping: 30,
};

/**
 * Bouncy spring for playful micro-interactions (likes, favorites)
 */
export const springBouncy = {
  type: "spring" as const,
  stiffness: 500,
  damping: 15,
};

// ============================================================================
// EASING CONFIGURATIONS
// ============================================================================

/**
 * Smooth ease for opacity and scale transitions
 */
export const easeSmooth = {
  duration: 0.2,
  ease: "easeOut" as const,
};

/**
 * Fade for the Sous Chef changed-words highlight (in and out)
 */
export const highlightFade = {
  duration: 0.3,
};

/**
 * Layout transition for expanding/collapsing elements
 */
export const easeLayout = {
  duration: 0.3,
  ease: "easeInOut" as const,
};

/**
 * Image reveal transition — slightly longer for a smooth fade-in on load
 */
export const easeImageReveal = {
  duration: 0.4,
  ease: "easeOut" as const,
};

// ============================================================================
// COMMON VARIANTS
// ============================================================================

/**
 * Fade in/out animation
 */
export const fadeVariants: Variants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: easeSmooth },
  exit: { opacity: 0, transition: easeSmooth },
};

export const crossfadeVariants: Variants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: easeLayout },
  exit: { opacity: 0, transition: easeLayout },
};

/**
 * Fade + slide up animation (for cards, sections)
 */
export const fadeSlideUpVariants: Variants = {
  hidden: {
    opacity: 0,
    y: 40,
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: springConfig,
  },
};

/**
 * Scale + fade animation (for modals, popovers)
 */
export const scaleVariants: Variants = {
  hidden: {
    opacity: 0,
    scale: 0.95,
  },
  visible: {
    opacity: 1,
    scale: 1,
    transition: springConfig,
  },
  exit: {
    opacity: 0,
    scale: 0.95,
    transition: easeSmooth,
  },
};

/**
 * Exit animation for list items being deleted
 */
export const listItemExitVariants: Variants = {
  exit: {
    opacity: 0,
    scale: 0.95,
    height: 0,
    marginBottom: 0,
    transition: {
      opacity: easeSmooth,
      scale: easeSmooth,
      height: { ...easeLayout, delay: 0.1 },
      marginBottom: { ...easeLayout, delay: 0.1 },
    },
  },
};

// ============================================================================
// CONTAINER/STAGGER VARIANTS
// ============================================================================

/**
 * Container for staggered children animations
 * @param staggerDelay - Delay between each child animation (default: 0.1s)
 */
export const staggerContainerVariants = (staggerDelay: number = 0.1): Variants => ({
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: staggerDelay,
    },
  },
});

/**
 * Item variant to be used with staggerContainerVariants
 */
export const staggerItemVariants: Variants = fadeSlideUpVariants;

// ============================================================================
// TILE FLY-IN CONFIG
// ============================================================================

export const tileFlyInConfig = {
  staggerWindow: 0.55,
  scale: 1.15,
  xRange: 32,
  yRange: 36,
  transition: {
    type: "spring" as const,
    stiffness: 380,
    damping: 22.5,
  },
};

// ============================================================================
// LOOPING / IDLE ANIMATIONS
// ============================================================================

export const steamIdleConfig = {
  lid: {
    animate: {
      y: [0, -2, -1.5, -1.5, -1, -0.5, 0],
      rotate: [0, 5, -4, 3, -1.5, 0, 0],
    },
    transition: {
      duration: 1,
      delay: 2,
      times: [0, 0.15, 0.3, 0.45, 0.6, 0.8, 1],
      repeat: Infinity,
      repeatDelay: 4,
      ease: "easeOut" as const,
    },
  },
  wisp1: {
    animate: { y: [0, -3, 0], opacity: [0.5, 0.9, 0.5] },
    transition: {
      duration: 2.5,
      repeat: Infinity,
      ease: "easeInOut" as const,
    },
  },
  wisp2: {
    animate: { y: [0, -4, 0], opacity: [0.3, 0.85, 0.3] },
    transition: {
      duration: 3,
      delay: 0.8,
      repeat: Infinity,
      ease: "easeInOut" as const,
    },
  },
  wisp3: {
    animate: { y: [0, -3, 0], opacity: [0.5, 0.9, 0.5] },
    transition: {
      duration: 2.7,
      delay: 1.5,
      repeat: Infinity,
      ease: "easeInOut" as const,
    },
  },
};

export const cookingIdleConfig = {
  fork: {
    animate: { rotate: [0, 12, -8, 4, 0] },
    transition: {
      duration: 0.5,
      delay: 1.5,
      times: [0, 0.2, 0.5, 0.75, 1],
      repeat: Infinity,
      repeatDelay: 4,
      ease: "easeOut" as const,
    },
  },
  knife: {
    animate: { rotate: [0, -12, 8, -4, 0] },
    transition: {
      duration: 0.5,
      delay: 1.5,
      times: [0, 0.2, 0.5, 0.75, 1],
      repeat: Infinity,
      repeatDelay: 4,
      ease: "easeOut" as const,
    },
  },
};

export const choppingIdleConfig = {
  knife: {
    animate: {
      y: [0, 6, 1, 6, 1, 6, 0],
      rotate: [-25, 0, -18, 0, -18, 0, -25],
    },
    transition: {
      duration: 1.6,
      times: [0, 0.08, 0.2, 0.28, 0.4, 0.48, 0.62],
      repeat: Infinity,
      ease: "easeOut" as const,
    },
  },
};

// ============================================================================
// TIMELINE SEQUENCE
// ============================================================================

const timelineStagger = 0.35;

export const timelineDotVariants: Variants = {
  hidden: { scale: 0, opacity: 0 },
  visible: (i: number) => ({
    scale: 1,
    opacity: 1,
    transition: { delay: i * timelineStagger, ...springSnappy },
  }),
};

export const timelineContentVariants: Variants = {
  hidden: { opacity: 0, y: 12 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * timelineStagger + 0.1, ...springConfig },
  }),
};

export const timelineLineVariants: Variants = {
  hidden: { scaleY: 0 },
  visible: (i: number) => ({
    scaleY: 1,
    transition: {
      delay: i * timelineStagger + 0.17,
      duration: 0.16,
      ease: "easeOut" as const,
    },
  }),
};
